import type { FormFieldNestedListValues } from '@ikegps/core-api';
import type { CollectionModelFieldNestedList, FormModelFieldNestedList } from '@ikegps/core-shared';
import { action, computed, override, makeObservable } from 'mobx';
import { type MultiChooserGroup, type MultiChooserGroupItem, MultiChooserState } from './multi.list.chooser.state';

export class NestedListChooserState extends MultiChooserState {
	field: CollectionModelFieldNestedList;
	formField: FormModelFieldNestedList;

	constructor(field: CollectionModelFieldNestedList) {
		super(field.schema.displayName);

		makeObservable(this);

		this.field = field;
		this.formField = field.schema;

		this.setValue(this.field.value);
	}

	@computed override get displayValue(): string {
		return this.field.hasValue() ? this.field.displayValue : null;
	}

	@computed override get groups(): MultiChooserGroup[] {
		const groups: MultiChooserGroup[] = [];

		const selections = this.getSelections(this.value);

		let group = this.formField.options;
		for (const selection of selections) {
			if (group == null) {
				throw new Error(`NestedList group not found for selection "${selection}"`);
			}

			const items: MultiChooserGroupItem[] = group.values.map((groupValue, i) => ({
				key: groupValue.value,
				value: groupValue.value,
				color: groupValue.color,
			}));
			const selectedValue = group.values.find((groupValue) => String(groupValue.value) === selection);
			if (selectedValue == null) {
				items.unshift({ key: selection, value: selection + '*' });
			}

			groups.push({
				title: group.title,
				items,
				selected: selectedValue != null ? selectedValue.value : items[0].value,
			});

			group = selectedValue != null ? selectedValue.options : null;
		}

		return groups;
	}

	@override override setValue(values: string[]): void {
		if (values == null || values.length === 0) {
			this._value = null;
			return;
		}

		this._value = this.getSelections(values);
	}

	@action selectDefaultValue() {
		this._value = this.getSelections([]);
	}

	//We need to override the generic getAllOptions function as the nested list need to keep the value of all the selected nodes instead of only the last one.
	override getAllOptions(): MultiChooserGroupItem[][] {
		let options = this.formField.options.values;

		let allOptions: MultiChooserGroupItem[][] = [];
		for (let option of options) {
			NestedListChooserState.flatten(option, [], allOptions);
		}

		return allOptions;
	}

	private static flatten(
		node: FormFieldNestedListValues,
		path: MultiChooserGroupItem[] = [],
		result: MultiChooserGroupItem[][] = []
	) {
		let item: MultiChooserGroupItem = { key: node.value, value: node.value };
		if (!node.options) {
			result.push([...path, item]);
		} else {
			for (const child of node.options.values) {
				NestedListChooserState.flatten(child, [...path, item], result);
			}
		}
		return result;
	}

	private getSelections(values: string[]): string[] {
		values = values || [];

		const selections: string[] = [];

		let group = this.formField.options;
		for (const value of values) {
			selections.push(String(value));

			const selectedValue = group.values.find((f) => String(f.value) === value);
			group = selectedValue != null ? selectedValue.options : null;
		}

		const previousValues = this.value != null ? this.value : [];
		while (group != null && group.values.length > 0) {
			let selectedValue = group.values[0];

			// Try to match existing selection in new group
			const previousValue = previousValues[selections.length];
			if (previousValue != null) {
				// Use == to compare strings with numbers untill we fix typing
				const equalValue = group.values.find((v) => v.value == previousValue); // eslint-disable-line
				if (equalValue != null) {
					selectedValue = equalValue;
				}
			}

			selections.push(String(selectedValue.value));

			group = selectedValue.options;
		}

		return selections;
	}
}
